import React, { useState, useMemo } from 'react'
import { useQuery, useMutation } from 'jsonapi-react'
import { Calendar } from 'react-feather'
import { add as addTime, set as setTime, format, differenceInMinutes } from 'date-fns'
import posthog from 'posthog-js'

import Input from 'app/theme/forms/Input'
import Location from 'app/theme/forms/Location'
import Select from 'app/theme/forms/Select'
import DatePicker from 'app/theme/date-picker/DatePicker'
import ConfirmationModal from 'app/theme/ConfirmationModal'
import TimePicker, { isStartTimeAfterEndTime, mergeTime } from 'app/theme/date-picker/TimePickerV2'
import LeadError from './LeadError'

export default function EditLeadForm(props) {
  const {
    lead,
    close
  } = props

  const [confirmCancel, setConfirmCancel] = useState(false)
  const [confirmSave, setConfirmSave] = useState(false)

  const [updateLead, { isLoading, data, setData, error, errors }] = useMutation(['leads', lead.id, { token: lead.token }], {
    initialData: {...lead},
  })

  const [startTime, setStartTime] = useState(() => {
    if (data.start_date) {
      // on initialization, we set the start TIME to whatever is in the data.start_date's TIME
      return mergeTime(new Date(), data.start_date)
    } else {
      // otherwise. we set the start TIME to today at noon
      return setTime(new Date(), { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 })
    }
  })

  // separates the idea of end "date" and "time".
  // we are using data.end_date for the DATE, and endTime for the TIME
  const [endTime, setEndTime] = useState(() => {
    if (data.end_date) {
      // on initialization, we set the end TIME to whatever is in the data.end_date's TIME
      return mergeTime(new Date(), data.end_date)
    } else {
      // otherwise, we set the end TIME to today at 2pm
      return setTime(new Date(), { hours: 14, minutes: 0, seconds: 0, milliseconds: 0 })
    }
  })

  const [manualAddress, setManualAddress] = useState(false)
  const location_unknown = data.location_status === 'approximate'

  const countryQuery = useQuery(['countries', {
    sort: 'name',
    include: 'regions',
    page: {
      size: 999,
    },
    sort: [
      'name',
      'regions.name',
    ]
  }])

  const [countries, country, regions] = useMemo(() => {
    const countries = countryQuery.data || []
    const country = countries.find(c => c.code === data.address?.country_code)
    const regions = country?.regions || []

    return [countries, country, regions]
  }, [countryQuery.data, data.address?.country_code])

  const dateIsDirty = useMemo(() => {
    return (
      lead.start_date.toString() !== data.start_date.toString() 
      || lead.end_date.toString() !== data.end_date.toString()
    )
  }, [data.start_date, data.end_date])

  const eatersIsDirty = useMemo(() => {
    return lead.eaters !== data.eaters
  }, [data.eaters])

  const locationIsDirty = useMemo(() => {
    return (
      lead.address?.street !== data.address?.street 
      || lead.address.formatted !== data.address?.formatted 
      || lead.address?.city_name !== data.address?.city_name
      || lead.address?.zip !== data.address.zip
      || lead.address?.region_code !== data.address.region_code
      || lead.address?.country_code !== data.address?.country_code 
    )
  }, [data.address])

  let disableStart = false

  const submit = () => {
    return updateLead({
      eaters: data.eaters,
      start_date: data.start_date,
      end_date: data.end_date,
      address: data.address
    }).then(() => {
      posthog.capture('event_edit_submitted', { 
        guest_count_changed: eatersIsDirty, 
        date_changed: dateIsDirty, 
        location_changed:  locationIsDirty
      })
      close()
    })
  }

  return (
    <div>
      <h2 className='text-xl font-normal text-black font-platform'>
        Edit Event Information
      </h2>

      <div className='my-4'>
        <label className='text-lg font-hairline font-platform text-black pl-5'>Guest Count</label>
        <Input
          type="number"
          value={data.eaters}
          min={1}
          data-test-id='eaters'
          placeholder='Enter number'
          classNames={{
            div: 'flex-1 w-full mx-1 px-4 py-2 rounded-full border',
            input: 'w-full outline-none font-platform text-black text-lg font-hairline'
          }}
          onChange={eaters => {
            setData(prev => ({
              ...prev,
              eaters,
            }))
          }}
        />
      </div>

      <div className='my-4'>
        <label className='text-lg font-hairline font-platform text-black pl-5'>Event Date</label>
        <div>
          <DatePicker
            autoFocus
            placeholder="mm/dd/yyyy"
            icon={<Calendar size={16} className="text-blue"/>}
            label={false}
            data-test-id='start_date'
            value={data.start_date}
            disableBefore={disableStart}
            classNames={{
              div: 'w-full px-4 py-2 rounded-full border',
              input: 'w-full outline-none text-lg font-hairline font-platform text-black',
              icon: 'text-blue right-auto left-0 mr-2'
            }}
            onChange={date => {
              let start_date = mergeTime(date, startTime)
              let end_date = data.end_date || start_date

              if (endTime.getHours() < 12) {
                end_date = mergeTime(addTime(date, { days: 1 }), endTime)
              } else {
                end_date = mergeTime(date, endTime)
              }

              setData(prev => ({
                ...prev,
                start_date,
                end_date,
              }))
            }}
          />
        </div>
        <label className='text-lg font-hairline font-platform text-black pl-5 pt-2 inline-block'>From</label>
        <TimePicker
          placeholder="From"
          className={[
            'w-full outline-none text-lg font-hairline font-platform text-black',
            'px-4 py-2 rounded-full border'
          ]}
          divClassName="flex-1"
          value={startTime}
          error={errors?.start_time}
          onChange={start_time => {
            // go ahead and update the time tracking state
            setStartTime(start_time)
            
            // Store the value of endTime so we don't mutate it
            let end_time = endTime

            // If a user sets the start time to any value AFTER the end time. 
            if (isStartTimeAfterEndTime(start_time, end_time)) {
              // Set the end time to two hours AFTER the start time
              const minutes = 120
              end_time = addTime( start_time, { minutes } )
              setEndTime(end_time)
            }

            if (data.start_date) {
              // update current data start/end dates by merging current with the new TIME values
              const newStartDate = mergeTime(data.start_date, start_time)
              const newEndDate = data.multiday || end_time.getHours() >= 12 
                ? mergeTime(data.end_date, end_time)
                : mergeTime(addTime(data.start_date, { days: 1 }), end_time)

              // update the data state with the new start/end dates and return
              setData(prev => ({
                ...prev,
                start_date: newStartDate,
                end_date: newEndDate
              }))
            }
          }}
        />
        <label className='text-lg font-hairline font-platform text-black pl-5 pt-2 inline-block'>To</label>
        <TimePicker
          placeholder="To (optional)"
          className={[
            'w-full outline-none text-lg font-hairline font-platform text-black',
            'px-4 py-2 rounded-full border'
          ]}
          value={endTime}
          minTime={ data.multiday ? undefined : startTime}
          maxTime={ data.multiday ? undefined : addTime(startTime, { hours: 23, minutes: 45 } ) }
          error={errors?.end_time}
          onChange={end_time => {
            setEndTime(end_time)

            if (!data.end_date) {
              return
            }

            let end_date

            if (data.multiday) {
              end_date = mergeTime(data.end_date, end_time)
            } else if (end_time.getHours() >= 12 ) {
              end_date = mergeTime(data.start_date, end_time)
            } else {
              // if the end time is BEFORE noon,
              // this means that the user intends the event to end in the AM of the next day
              // so we add a day to the end_date
              end_date = mergeTime(addTime(data.start_date, { days: 1 }), end_time)
            }

            setData(prev => ({
              ...prev,
              end_date,
            }))
          }}
        />
      </div>

      <div>
        <label className='text-lg font-hairline font-platform text-black pl-5'>Event Location</label>
        { location_unknown ? (
          <div>
            {/* A user needs to be able to switch to a known location */}
            <button 
              className='btn rounded-full px-4 py-1 mb-2 btn-white text-black'
              onClick={() => {
                setData(prev => ({
                  ...prev,
                  location_status: 'confirmed',
                }))
              }}
            >
              I know my event address now
            </button>

            <div className="mb-3 grid grid-cols-1 gap-4">
              { manualAddress ? (
                <div className="grid gap-4">
                  {countryQuery.isLoading ? (
                    <div className="">
                      <Spinner className="text-blue w-10 h-10 mx-auto my-5"/>
                    </div>
                  ) : countries.length ? (
                    <>
                      <div>
                        <Location
                          types={['(cities)']}
                          value={data.address.formatted || data.address?.street}
                          data-test-id='address'
                          placeholder='Enter or search location'
                          classNames={{
                            div: 'w-full px-4 py-2 rounded-full border',
                            input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                          }}
                          onChange={(location, address) => {
                            setData(prev => ({
                              ...prev,
                              address: address || {
                                ...prev.address,
                                street: location,
                                formatted: location,
                                location: null,
                              }
                            }))
                          }}
                        />
                        {errors?.street && (
                          <LeadError
                            message={errors.street}
                            className="text-center"
                          />
                        )}
                      </div>
                      <div>
                        <Input
                          placeholder='City'
                          value={data.address?.city_name}
                          data-test-id='city_name'
                          classNames={{
                            div: 'w-full px-4 py-2 rounded-full border',
                            input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                          }}
                          onChange={city_name => {
                            setData(prev => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                city_name: city_name,
                                location: null
                              }
                            }))
                          }}
                        />
                        {errors?.city_name && (
                          <LeadError
                            message={errors.city_name}
                            className="text-center"
                          />
                        )}
                      </div>
                      <div className='grid gap-4 md:grid-cols-2'>
                        {(country?.code === 'US' && regions?.length) ? (
                          <div>
                            <Select
                              placeholder="Choose A State"
                              value={data.address?.region_code}
                              classNames={{
                                select: 'w-full px-4 py-2 rounded-full border w-full outline-none font-platform text-black text-lg font-hairline',
                              }}
                              options={regions?.map(region => ({
                                label: region.name,
                                value: region.code
                              }))}
                              onChange={region_code => {
                                setData(prev => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    region_code
                                  },
                                }))
                              }}
                            />
                            {errors?.region_code && (
                              <LeadError
                                message={errors.region_code}
                                className="text-center"
                              />
                            )}
                          </div>
                        ) : (
                          <div>
                            <Input
                              placeholder={`${country?.region_label || 'State'}`}
                              value={data.address?.region_code}
                              data-test-id='region'
                              classNames={{
                                div: 'w-full px-4 py-2 rounded-full border',
                                input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                              }}
                              onChange={region_code => {
                                setData(prev => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    region_code,
                                  },
                                }))
                              }}
                            />
                            {errors?.region_code && (
                              <LeadError
                                message={errors.region_code}
                                className="text-center"
                              />
                            )}
                          </div>
                        )}
                        <div>
                          <Select
                            placeholder="Choose A Country"
                            value={data.address?.country_code}
                            classNames={{
                              select: 'w-full px-4 py-2 rounded-full border w-full outline-none font-platform text-black text-lg font-hairline',
                            }}
                            options={countries?.map((country) => ({
                              label: country.name,
                              value: country.code
                            }))}
                            onChange={selection => {
                              const selected = countries.find(c => c.code === selection)

                              if (!selected) return

                              setData(prev => ({
                                ...prev,
                                address: {
                                  ...prev.address,
                                  country_name: selected.name,
                                  country_code: selected.code,
                                  region_name: null,
                                  region_code: null,
                                  location: null,
                                },
                              }))
                            }}
                          />
                          {errors?.country && (
                            <LeadError
                              message={errors.country}
                              className="text-center"
                            />
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="font-bold px-8 py-3 text-center text-red-600">
                      An unexpected error occurred. Please try to submit your details again in a few minutes.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <Location
                      types={['(cities)']}
                      value={data.address?.formatted || data.address?.street}
                      data-test-id='address'
                      placeholder='Enter or search location'
                      classNames={{
                        div: 'w-full px-4 py-2 rounded-full border',
                        input: 'w-full outline-none font-platform text-black text-lg font-hairline',
                      }}
                      onChange={(location, address) => {
                        if (address) {
                          setManualAddress(true)
                        }
                        setData(prev => ({
                          ...prev,
                          address: address || {
                            ...prev.address,
                            street: location,
                            formatted: location,
                            location: null,
                          }
                        }))
                      }}
                    />
                    {errors?.street && (
                      <LeadError
                        message={errors.street}
                        className="text-center"
                      />
                    )}
                  </div>

                  <p 
                    className="w-full text-center text-lg text-blue font-bold"
                    onClick={() => setManualAddress(true)}
                  >
                    Enter a manual address
                  </p>
                </>
              )}
            </div>
          </div>
        ) : data.location_status === 'confirmed' ? (
          <div>
            <div className="mb-3 grid grid-cols-1 gap-4">
              { manualAddress ? (
                <div className="grid gap-4">
                  {countryQuery.isLoading ? (
                    <div className="">
                      <Spinner className="text-blue w-10 h-10 mx-auto my-5"/>
                    </div>
                  ) : countries.length ? (
                    <>
                      <div>
                        <Location
                          value={data.address?.street}
                          data-test-id='address'
                          placeholder='Enter or search location'
                          classNames={{
                            div: 'w-full px-4 py-2 rounded-full border',
                            input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                          }}
                          onChange={(location, address) => {
                            setData(prev => ({
                              ...prev,
                              address: address || {
                                ...prev.address,
                                street: location,
                                location: null,
                              }
                            }))
                          }}
                        />
                        {errors?.street && (
                          <LeadError
                            message={errors.street}
                            className="text-center"
                          />
                        )}
                      </div>
                      <div>
                        <Input
                          placeholder='City'
                          value={data.address?.city_name}
                          data-test-id='city_name'
                          classNames={{
                            div: 'w-full px-4 py-2 rounded-full border',
                            input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                          }}
                          onChange={city_name => {
                            setData(prev => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                city_name: city_name,
                                location: null
                              }
                            }))
                          }}
                        />
                        {errors?.city_name && (
                          <LeadError
                            message={errors.city_name}
                            className="text-center"
                          />
                        )}
                      </div>
                      <div className='grid gap-4 md:grid-cols-2'>
                        {(country?.code === 'US' && regions?.length) ? (
                          <div>
                            <Select
                              placeholder="Choose A State"
                              value={data.address?.region_code}
                              classNames={{
                                select: 'w-full px-4 py-2 rounded-full border w-full outline-none font-platform text-black text-lg font-hairline',
                              }}
                              options={regions?.map(region => ({
                                label: region.name,
                                value: region.code
                              }))}
                              onChange={region_code => {
                                setData(prev => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    region_code
                                  },
                                }))
                              }}
                            />
                            {errors?.region_code && (
                              <LeadError
                                message={errors.region_code}
                                className="text-center"
                              />
                            )}
                          </div>
                        ) : (
                          <div>
                            <Input
                              placeholder={`${country?.region_label || 'State'}`}
                              value={data.address?.region_code}
                              data-test-id='region'
                              classNames={{
                                div: 'w-full px-4 py-2 rounded-full border',
                                input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                              }}
                              onChange={region_code => {
                                setData(prev => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    region_code,
                                  },
                                }))
                              }}
                            />
                            {errors?.region_code && (
                              <LeadError
                                message={errors.region_code}
                                className="text-center"
                              />
                            )}
                          </div>
                        )}
                        <div>
                          <Input
                            placeholder={country?.zip_label || 'Zip'}
                            value={data.address?.zip}
                            data-test-id='zip'
                            classNames={{
                              div: 'w-full px-4 py-2 rounded-full border',
                              input: 'w-full outline-none font-platform text-black text-lg font-hairline'
                            }}
                            onChange={zip => {
                              setData(prev => ({
                                ...prev,
                                address: {
                                  ...prev.address,
                                  zip: zip,
                                  location: null
                                }
                              }))
                            }}
                          />
                          {errors?.region_code && (
                            <LeadError
                              message={errors.zip}
                              className="text-center"
                            />
                          )}
                        </div>
                      </div>
                      <div>
                        <Select
                          placeholder="Choose A Country"
                          value={data.address?.country_code}
                          classNames={{
                            select: 'w-full px-4 py-2 rounded-full border w-full outline-none font-platform text-black text-lg font-hairline',
                          }}
                          options={countries?.map((country) => ({
                            label: country.name,
                            value: country.code
                          }))}
                          onChange={selection => {
                            const selected = countries.find(c => c.code === selection)

                            if (!selected) return

                            setData(prev => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                country_name: selected.name,
                                country_code: selected.code,
                                region_name: null,
                                region_code: null,
                                location: null,
                              },
                            }))
                          }}
                        />
                        {errors?.country && (
                          <LeadError
                            message={errors.country}
                            className="text-center"
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="font-bold px-8 py-3 text-center text-red-600">
                      An unexpected error occurred. Please try to submit your details again in a few minutes.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <Location
                      value={data.address?.street}
                      data-test-id='address'
                      placeholder='Enter or search location'
                      classNames={{
                        div: 'w-full px-4 py-2 rounded-full border',
                        input: 'w-full outline-none font-platform text-black text-lg font-hairline',
                      }}
                      onChange={(location, address) => {
                        if (address) {
                          setManualAddress(true)
                        }
                        setData(prev => ({
                          ...prev,
                          address: address || {
                            ...prev.address,
                            street: location,
                            location: null,
                          }
                        }))
                      }}
                    />

                    {errors?.street && (
                      <LeadError
                        message={errors.street}
                        className="text-center"
                      />
                    )}
                  </div>

                  <p 
                    className="w-full text-center text-lg text-blue font-bold"
                    onClick={() => setManualAddress(true)}
                  >
                    Enter a manual address
                  </p>
                </>
              )}
            </div>

          </div>
        ) : (
          <div className='grid grid-cols-1 gap-4'>
            There has been a problem. Please try again later.
          </div>
        )}
      </div>

      <div className="pt-4">
        <button 
          className={[
            'btn rounded-full px-4 py-1 btn-blue text-white mr-2', 
            isLoading && 'opacity-50 cursor-not-allowed',
            eatersIsDirty || dateIsDirty || locationIsDirty ? '' : 'opacity-50 cursor-not-allowed'
          ]}
          disabled={isLoading}
          onClick={() => setConfirmSave(true)}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
        <button 
          className='btn rounded-full px-4 py-1 btn-white text-black' 
          onClick={() => setConfirmCancel(true)}
        >
          Cancel
        </button>
      </div>
      {confirmSave && (
        <ConfirmationModal
          title = 'Are you sure you want to update your event settings?'
          message = ''
          confirmButtonText='Yes, update my event settings'
          cancelButtonText='Cancel'
          classNames={{
            isFullScreen: false,
            modal: 'flex self-center items-center justify-center p-8 bg-white min-h-screen sm:min-h-0',
            cancelButton: 'w-full btn text-black btn-white py-2 rounded-full',
            confirmButton: 'w-full btn btn-blue py-2 rounded-full'
          }}
          onCancel={() => {
            setConfirmSave(false)
          }}
          closeModal={() => {
            setConfirmSave(false)
          }}
          onConfirm={() => {
            setConfirmSave(false)
            submit()
          }}
        />
      )}
      {confirmCancel && (
        <ConfirmationModal
          title = 'Are you sure you want to cancel your changes?'
          message = 'Your changes will be reset.'
          confirmButtonText='Yes'
          cancelButtonText='No'
          classNames={{
            isFullScreen: false,
            modal: 'flex self-center items-center justify-center p-8 bg-white min-h-screen sm:min-h-0',
            cancelButton: 'w-full btn text-black btn-white py-2 rounded-full',
            confirmButton: 'w-full btn btn-blue py-2 rounded-full'
          }}
          onCancel={() => {
            setConfirmCancel(false)
          }}
          closeModal={() => {
            setConfirmCancel(false)
          }}
          onConfirm={() => {
            setConfirmCancel(false)
            posthog.capture('event_edit_canceled')
            close()
          }}
        />
      )}
    </div>
  )
}